import React, { useState, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { ContractClient } from './contract';
import { ReadOnlyContractClient } from './contract-readonly';
import { CountryManagementOverlay } from './CountryManagementOverlay';
import { iso2ToNumeric, iso3ToIso2 } from './countryCodes';
import { getFlagUrl } from './utils/countryVaultUtils';
import { AddressInputWithQR } from './components/AddressInputWithQR';
import OrganizationsSection from './components/organizations/OrganizationsSection';

interface AdminPanelProps {
  contractClient: ContractClient;
  allowedCountries: Set<number>;
  defaultAllowAll: boolean;
  onCountryToggle: () => void;
  onDefaultAllowAllChange?: (value: boolean) => void;
  onCountryAllowedChange?: (countryCode: number, allowed: boolean) => void;
  map: mapboxgl.Map | null;
  minimized?: boolean;
  onToggleMinimize?: () => void;
  onAdminChanged?: () => void;
  walletAddress?: string | null;
  mainAdminAddress?: string | null;
  onManageCountry?: (country: { code: number; name: string }) => void;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({
  contractClient,
  allowedCountries,
  defaultAllowAll,
  onCountryToggle,
  onDefaultAllowAllChange,
  onCountryAllowedChange,
  map,
  minimized = false,
  onToggleMinimize,
  onAdminChanged,
  walletAddress,
  mainAdminAddress,
  onManageCountry,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countryList, setCountryList] = useState<Array<{ code: number; name: string; iso2: string }>>([]);
  const [showChangeAdminOverlay, setShowChangeAdminOverlay] = useState(false);
  const [newAdminAddress, setNewAdminAddress] = useState('');
  const [isChangingAdmin, setIsChangingAdmin] = useState(false);
  const [countryAdmins, setCountryAdmins] = useState<Map<number, string>>(new Map());
  const [selectedCountryForAdmin, setSelectedCountryForAdmin] = useState<number | null>(null);
  const [showCountryAdminOverlay, setShowCountryAdminOverlay] = useState(false);
  const [newCountryAdminAddress, setNewCountryAdminAddress] = useState('');
  const [isSettingCountryAdmin, setIsSettingCountryAdmin] = useState(false);
  const [showCountryManagement, setShowCountryManagement] = useState(false);
  const [selectedCountryForManagement, setSelectedCountryForManagement] = useState<{ code: number; name: string } | null>(null);
  
  // Check if current user is main admin
  const isMainAdmin: boolean = !!(walletAddress && mainAdminAddress && walletAddress === mainAdminAddress);
  
  // Handle opening country management overlay
  const handleManageCountry = async (country: { code: number; name: string }) => {
    // Load country admin if not already loaded
    if (!countryAdmins.has(country.code)) {
      await loadCountryAdmin(country.code);
    }
    // Use callback if provided, otherwise use local state (for backward compatibility)
    if (onManageCountry) {
      onManageCountry(country);
    } else {
      setSelectedCountryForManagement(country);
      setShowCountryManagement(true);
    }
  };

  // Load country admin for a specific country (on-demand)
  const loadCountryAdmin = async (countryCode: number) => {
    if (!isMainAdmin) return;
    
    try {
      // Use read-only client to avoid Freighter prompts
      const readOnlyClient = new ReadOnlyContractClient();
      const admin = await readOnlyClient.getCountryAdmin(countryCode);
      if (admin) {
        setCountryAdmins(prev => new Map(prev).set(countryCode, admin));
      } else {
        setCountryAdmins(prev => {
          const next = new Map(prev);
          next.delete(countryCode);
          return next;
        });
      }
    } catch (error) {
      // console.debug(`[AdminPanel] No admin for country ${countryCode}`);
    }
  };

  // Proactively load ALL country admins when the main admin opens the panel.
  // Runs in batches of 20 concurrent requests to avoid overwhelming the RPC,
  // and updates state incrementally so assigned admins appear as they load.
  useEffect(() => {
    if (!isMainAdmin || countryList.length === 0) return;

    let cancelled = false;
    const readOnlyClient = new ReadOnlyContractClient();

    const loadAll = async () => {
      const BATCH = 20;
      for (let i = 0; i < countryList.length; i += BATCH) {
        if (cancelled) break;
        const slice = countryList.slice(i, i + BATCH);
        const results = await Promise.allSettled(
          slice.map(c =>
            readOnlyClient.getCountryAdmin(c.code)
              .then(admin => ({ code: c.code, admin }))
              .catch(() => ({ code: c.code, admin: null }))
          )
        );
        if (cancelled) break;
        setCountryAdmins(prev => {
          const next = new Map(prev);
          results.forEach(r => {
            if (r.status === 'fulfilled') {
              if (r.value.admin) next.set(r.value.code, r.value.admin);
              // leave absent keys out of the map (= no admin)
            }
          });
          return next;
        });
      }
    };

    loadAll();
    return () => { cancelled = true; };
  }, [isMainAdmin, countryList]);

  // Load countries from GeoJSON file
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
        if (response.ok) {
          const geojson = await response.json();
          if (geojson.features) {
            const countries = geojson.features
              .map((feature: any) => {
                // Try ISO_NUMERIC first, then convert from ISO2/ISO3
                let code = feature.properties?.ISO_NUMERIC;
                const name = feature.properties?.name || feature.properties?.NAME;
                // Check both properties.id and feature-level id (which is ISO3)
                const iso3 = feature.id || feature.properties?.id;
                const iso2 = feature.properties?.ISO2;
                
                // If no ISO_NUMERIC, try to convert from ISO2 or ISO3
                if (!code) {
                  if (iso2) {
                    code = iso2ToNumeric(iso2);
                  } else if (iso3) {
                    // Convert ISO3 to ISO2 first, then to numeric
                    const iso2FromIso3 = iso3ToIso2(iso3);
                    if (iso2FromIso3) {
                      code = iso2ToNumeric(iso2FromIso3);
                    }
                  }
                }
                
                if (code && name) {
                  return { code: Number(code), name: String(name), iso2: String(iso2 || iso3ToIso2(iso3) || '') };
                }
                return null;
              })
              .filter((c: any) => c !== null)
              .sort((a: any, b: any) => a.name.localeCompare(b.name));
            setCountryList(countries);
            // console.log('[AdminPanel] Loaded', countries.length, 'countries from GeoJSON');
          }
        } else {
          // console.warn('[AdminPanel] Failed to load countries.geojson');
        }
      } catch (error) {
        // console.error('[AdminPanel] Error loading countries:', error);
      }
    };
    loadCountries();
  }, []);

  // Determine if a country is currently allowed based on the policy
  const isCountryAllowed = (countryCode: number): boolean => {
    if (defaultAllowAll) {
      return !allowedCountries.has(countryCode);
    } else {
      return allowedCountries.has(countryCode);
    }
  };

  const filteredCountries = countryList.filter((country) => {
    const q = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(q) ||
      country.iso2.toLowerCase().includes(q)
    );
  });

  const allowedCount = filteredCountries.filter(c => isCountryAllowed(c.code)).length;
  const deniedCount  = filteredCountries.length - allowedCount;

  const handleToggleCountry = async (countryCode: number) => {
    const country = countryList.find(c => c.code === countryCode);
    if (!country) return;
    setIsLoading(true);
    try {
      const currentlyAllowed = isCountryAllowed(countryCode);
      const newAllowed = !currentlyAllowed;

      // setCountryAllowed handles register-then-enable internally (idempotent)
      await contractClient.setCountryAllowed(countryCode, newAllowed);

      // Optimistic update — no list endpoint in V2, so update local state directly
      if (onCountryAllowedChange) {
        onCountryAllowedChange(countryCode, newAllowed);
      } else {
        onCountryToggle();
      }
    } catch (error: any) {
      alert(`Failed to update country policy: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleDefaultPolicy = async () => {
    const newValue = !defaultAllowAll;
    setIsLoading(true);
    try {
      await contractClient.setDefaultAllowAll(newValue);
      // Optimistically update parent state — no on-chain getter exists for the global default flag.
      // Do NOT call onCountryToggle() here: fetchCountryPolicy() would re-read get_country_policy
      // (per-country, not the global flag), return null, and revert the state we just set.
      if (onDefaultAllowAllChange) {
        onDefaultAllowAllChange(newValue);
      }
    } catch (error: any) {
      alert(`Failed to update default policy: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeAdmin = async () => {
    if (!newAdminAddress || newAdminAddress.length !== 56 || !newAdminAddress.startsWith('G')) {
      alert('Please enter a valid Stellar address (56 characters, starting with G)');
      return;
    }

    const confirmMessage = `Are you sure you want to transfer admin rights to:\n${newAdminAddress}\n\nThis will remove your admin privileges and you will be logged out.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsChangingAdmin(true);
    try {
      await contractClient.setAdmin(newAdminAddress);
      alert('Admin successfully changed! You will lose admin access.');
      setShowChangeAdminOverlay(false);
      setNewAdminAddress('');
      // Notify parent to re-check admin status
      if (onAdminChanged) {
        onAdminChanged();
      }
    } catch (error: any) {
      // console.error('Failed to change admin:', error);
      // alert(`Failed to change admin: ${error.message || error}`);
    } finally {
      setIsChangingAdmin(false);
    }
  };

  const handleSetCountryAdmin = async (countryCode: number) => {
    setSelectedCountryForAdmin(countryCode);
    // Load current admin for this country if not already loaded
    if (!countryAdmins.has(countryCode)) {
      await loadCountryAdmin(countryCode);
    }
    setShowCountryAdminOverlay(true);
    // Pre-fill with existing admin if any
    const existingAdmin = countryAdmins.get(countryCode);
    setNewCountryAdminAddress(existingAdmin || '');
  };

  const handleSaveCountryAdmin = async () => {
    if (!selectedCountryForAdmin) return;
    
    if (!newCountryAdminAddress || newCountryAdminAddress.length !== 56 || !newCountryAdminAddress.startsWith('G')) {
      alert('Please enter a valid Stellar address (56 characters, starting with G)');
      return;
    }

    setIsSettingCountryAdmin(true);
    try {
      if (newCountryAdminAddress.trim() === '') {
        // Remove country admin
        await contractClient.removeCountryAdmin(selectedCountryForAdmin);
        const newAdmins = new Map(countryAdmins);
        newAdmins.delete(selectedCountryForAdmin);
        setCountryAdmins(newAdmins);
        alert('Country admin removed successfully');
        // Refresh to confirm removal
        await loadCountryAdmin(selectedCountryForAdmin);
      } else {
        // Set country admin
        await contractClient.setCountryAdmin(selectedCountryForAdmin, newCountryAdminAddress);
        const newAdmins = new Map(countryAdmins);
        newAdmins.set(selectedCountryForAdmin, newCountryAdminAddress);
        setCountryAdmins(newAdmins);
        alert('Country admin set successfully');
        // Refresh country admin to ensure it's up to date
        await loadCountryAdmin(selectedCountryForAdmin);
      }
      setShowCountryAdminOverlay(false);
      setNewCountryAdminAddress('');
      setSelectedCountryForAdmin(null);
    } catch (error: any) {
      // console.error('Failed to set country admin:', error);
      // alert(`Failed to set country admin: ${error.message || error}`);
    } finally {
      setIsSettingCountryAdmin(false);
    }
  };

  const getCountryAdmin = (countryCode: number): string | null => {
    return countryAdmins.get(countryCode) || null;
  };

  // Country click handling is now done in App.tsx
  // Removed local click handler to avoid conflicts

  return (
    <div className="admin-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: minimized ? '0' : '12px', gap: '8px' }}>
        <h3 style={{ margin: 0, flex: 1, whiteSpace: 'nowrap' }}>Admin Panel</h3>
        {onToggleMinimize && (
          <button 
            className="icon-button" 
            onClick={onToggleMinimize} 
            title={minimized ? 'Expand' : 'Minimize'}
            style={{ fontSize: '10px', padding: '1px 3px', width: '18px', height: '18px', flexShrink: 0 }}
          >
            {minimized ? '▼' : '▲'}
          </button>
        )}
      </div>
      {!minimized && (
        <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <button
          role="switch"
          aria-checked={defaultAllowAll}
          onClick={handleToggleDefaultPolicy}
          disabled={isLoading}
          title={defaultAllowAll ? 'Click to deny all by default' : 'Click to allow all by default'}
          style={{
            width: '38px',
            height: '22px',
            borderRadius: '11px',
            border: 'none',
            outline: 'none',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            backgroundColor: defaultAllowAll ? '#22c55e' : '#6b7280',
            position: 'relative',
            transition: 'background-color 0.2s ease',
            padding: 0,
            flexShrink: 0,
            opacity: isLoading ? 0.5 : 1,
          }}
        >
          <span style={{
            position: 'absolute',
            top: '3px',
            left: defaultAllowAll ? '19px' : '3px',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            transition: 'left 0.2s ease',
            display: 'block',
            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
          }} />
        </button>
        <span
          style={{ fontSize: '13px', userSelect: 'none', cursor: isLoading ? 'default' : 'pointer' }}
          onClick={isLoading ? undefined : handleToggleDefaultPolicy}
        >
          Default: Allow All Countries
        </span>
      </div>
      <input
        type="text"
        placeholder="Search country..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={isLoading}
      />
      {/* Allowed / Denied summary */}
      {filteredCountries.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', fontSize: '11px' }}>
          <span style={{ color: '#4caf50', fontWeight: 600 }}>✓ {allowedCount} allowed</span>
          <span style={{ color: '#888' }}>·</span>
          <span style={{ color: '#f44336', fontWeight: 600 }}>✕ {deniedCount} denied</span>
          {searchTerm && <span style={{ color: '#888' }}>of {countryList.length} total</span>}
        </div>
      )}

      <div className="country-list">
        {filteredCountries.map((country) => {
          const isAllowed = isCountryAllowed(country.code);
          const countryAdmin = getCountryAdmin(country.code);
          const hasCountryAdmin = countryAdmin !== null;
          return (
            <div key={country.code} className="country-item" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                {/* Flag + name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flex: 1, minWidth: 0 }}>
                  {country.iso2 && (
                    <img
                      src={getFlagUrl(country.iso2)}
                      alt=""
                      style={{ width: '18px', height: 'auto', borderRadius: '2px', flexShrink: 0 }}
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{country.name}</span>
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <button
                    className="primary-button"
                    onClick={() => handleManageCountry(country)}
                    disabled={isLoading}
                    style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      backgroundColor: '#2196f3',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                    title="Manage Country"
                  >
                    Manage
                  </button>
                  <button
                    className={`country-toggle ${isAllowed ? 'allowed' : 'denied'}`}
                    onClick={() => handleToggleCountry(country.code)}
                    disabled={isLoading}
                    style={{ marginLeft: '4px' }}
                  >
                    {isAllowed ? 'Allowed' : 'Denied'}
                  </button>
                </div>
              </div>
              {isMainAdmin && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: '#666', marginTop: '2px', padding: '2px 0' }}>
                  <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {hasCountryAdmin && (
                      <span style={{ 
                        display: 'inline-block',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        flexShrink: 0
                      }} title="Has country admin" />
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hasCountryAdmin ? (
                        <>Admin: {countryAdmin.substring(0, 8)}...{countryAdmin.substring(48)}</>
                      ) : (
                        <>No country admin (uses main admin)</>
                      )}
                    </span>
                  </div>
                  <button
                    onClick={() => handleSetCountryAdmin(country.code)}
                    disabled={isLoading}
                    style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      marginLeft: '4px',
                      backgroundColor: hasCountryAdmin ? '#ff9800' : '#4caf50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                    title={hasCountryAdmin ? 'Change/Remove Country Admin' : 'Set Country Admin'}
                  >
                    {hasCountryAdmin ? 'Edit' : 'Set Admin'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
        Click on map to toggle countries
      </p>

      {/* Organizations — read-only here. Registration and status changes
          are country-scoped on chain, so they live in the per-country
          management overlay. */}
      <div style={{ marginTop: '16px' }}>
        <OrganizationsSection
          countryCode={null}
          signer={null}
          walletAddress={walletAddress || null}
          readOnly
        />
      </div>

      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
        <button
          className="primary-button"
          onClick={() => setShowChangeAdminOverlay(true)}
          disabled={isLoading}
          style={{ 
            width: '100%', 
            backgroundColor: '#d32f2f', 
            color: '#fff',
            fontSize: '12px',
            padding: '8px 16px'
          }}
        >
          Transfer Admin Rights
        </button>
        <p style={{ marginTop: '8px', fontSize: '11px', color: '#999', textAlign: 'center' }}>
          ⚠️ This will remove your admin access
        </p>
      </div>
        </>
      )}

      {/* Country Admin Overlay */}
      {showCountryAdminOverlay && selectedCountryForAdmin !== null && (
        <div 
          className="marker-popup-overlay" 
          onClick={() => !isSettingCountryAdmin && setShowCountryAdminOverlay(false)}
          style={{ zIndex: 3000 }}
        >
          <div className="marker-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <button 
              className="marker-popup-close" 
              onClick={() => !isSettingCountryAdmin && setShowCountryAdminOverlay(false)}
              disabled={isSettingCountryAdmin}
            >
              ×
            </button>
            <h3>Set Country Admin</h3>
            <div className="marker-popup-content">
              <div className="marker-popup-field">
                <label>Country:</label>
                {(() => {
                  const c = countryList.find(c => c.code === selectedCountryForAdmin);
                  return (
                    <span style={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      {c?.iso2 && (
                        <img
                          src={getFlagUrl(c.iso2)}
                          alt={c.iso2}
                          style={{ width: '20px', height: 'auto', borderRadius: '2px' }}
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      {c?.name || `Country ${selectedCountryForAdmin}`}
                      {c?.iso2 && <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>{c.iso2}</span>}
                    </span>
                  );
                })()}
              </div>
              <div className="marker-popup-field">
                <label>Country Admin Address:</label>
                <input
                  type="text"
                  value={newCountryAdminAddress}
                  onChange={(e) => setNewCountryAdminAddress(e.target.value)}
                  placeholder="GXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX (leave empty to remove)"
                  disabled={isSettingCountryAdmin}
                  style={{
                    width: '100%',
                    padding: '8px',
                    fontSize: '12px',
                    fontFamily: 'Courier New, monospace',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '4px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '10px', marginTop: '4px', display: 'block' }}>
                  Enter the Stellar address (56 characters, starting with G) of the country admin.
                  <br />
                  Leave empty to remove the country admin (reverts to main admin).
                </small>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button
                  className="primary-button"
                  onClick={handleSaveCountryAdmin}
                  disabled={isSettingCountryAdmin || (newCountryAdminAddress.trim() !== '' && (newCountryAdminAddress.length !== 56 || !newCountryAdminAddress.startsWith('G')))}
                  style={{ 
                    flex: 1,
                    backgroundColor: newCountryAdminAddress.trim() ? '#4caf50' : '#ff9800',
                    color: '#fff'
                  }}
                >
                  {isSettingCountryAdmin ? 'Saving...' : (newCountryAdminAddress.trim() ? 'Set Admin' : 'Remove Admin')}
                </button>
                <button
                  className="primary-button"
                  onClick={() => {
                    setShowCountryAdminOverlay(false);
                    setNewCountryAdminAddress('');
                    setSelectedCountryForAdmin(null);
                  }}
                  disabled={isSettingCountryAdmin}
                  style={{ 
                    flex: 1,
                    backgroundColor: '#666',
                    color: '#fff'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Admin Overlay */}
      {showChangeAdminOverlay && (
        <div 
          className="marker-popup-overlay" 
          onClick={() => !isChangingAdmin && setShowChangeAdminOverlay(false)}
          style={{ zIndex: 3000 }}
        >
          <div className="marker-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <button 
              className="marker-popup-close" 
              onClick={() => !isChangingAdmin && setShowChangeAdminOverlay(false)}
              disabled={isChangingAdmin}
            >
              ×
            </button>
            <h3>Transfer Admin Rights</h3>
            <div className="marker-popup-content">
              <div className="marker-popup-field">
                <label>Warning:</label>
                <span style={{ color: '#d32f2f', fontSize: '14px' }}>
                  Transferring admin rights will remove your admin access. You will need to reconnect with the new admin account to regain access.
                </span>
              </div>
              <div className="marker-popup-field">
                <AddressInputWithQR
                  value={newAdminAddress}
                  onChange={setNewAdminAddress}
                  placeholder="GXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                  disabled={isChangingAdmin}
                  label="New Admin Address:"
                  inputStyle={{
                    width: '100%',
                    padding: '8px',
                    fontSize: '12px',
                    fontFamily: 'Courier New, monospace',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginTop: '4px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '10px', marginTop: '4px', display: 'block' }}>
                  Enter the Stellar address (56 characters, starting with G) of the new admin
                </small>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button
                  className="primary-button"
                  onClick={handleChangeAdmin}
                  disabled={isChangingAdmin || !newAdminAddress}
                  style={{ 
                    flex: 1,
                    backgroundColor: '#d32f2f',
                    color: '#fff'
                  }}
                >
                  {isChangingAdmin ? 'Transferring...' : 'Confirm Transfer'}
                </button>
                <button
                  className="primary-button"
                  onClick={() => setShowChangeAdminOverlay(false)}
                  disabled={isChangingAdmin}
                  style={{ 
                    flex: 1,
                    backgroundColor: '#666',
                    color: '#fff'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Country Management Overlay - Only render if callback not provided (backward compatibility) */}
      {!onManageCountry && showCountryManagement && selectedCountryForManagement && (
        <CountryManagementOverlay
          isOpen={showCountryManagement}
          onClose={() => {
            setShowCountryManagement(false);
            setSelectedCountryForManagement(null);
          }}
          countryCode={selectedCountryForManagement.code}
          countryName={selectedCountryForManagement.name}
          contractClient={contractClient}
          walletAddress={walletAddress || null}
          mainAdminAddress={mainAdminAddress || null}
          isMainAdmin={isMainAdmin}
          map={map}
          onPolicyChanged={onCountryToggle}
        />
      )}
    </div>
  );
};
